import { useMemo, useState } from 'react';
import { where } from 'firebase/firestore';
import type { PromptLogDoc } from '@spirits/shared';
import { useCollection } from '../hooks/useCollection';

// DEBUG — a popup of every outgoing LLM call for this run (dialogue, reaction,
// scene fill, recap, suggestion), latest on top, with the full prompt + response.
interface Props {
  storyId: string;
}

function millis(t: unknown): number {
  const v = t as { seconds?: number; nanoseconds?: number } | number | undefined;
  if (typeof v === 'number') return v;
  if (v && typeof v.seconds === 'number') return v.seconds * 1000 + (v.nanoseconds ?? 0) / 1e6;
  return 0;
}

const TYPE_COLOR: Record<string, string> = {
  'scene-fill': 'text-amber-300/80',
  recap: 'text-emerald-300/80',
  dialogue: 'text-sky-300/80',
  reaction: 'text-violet-300/80',
  suggest: 'text-slate-400',
};

export function ScenePrompts({ storyId }: Props) {
  const [open, setOpen] = useState(false);
  const { data: logs } = useCollection<PromptLogDoc>('prompts', where('storyId', '==', storyId));
  const ordered = useMemo(
    () => [...logs].sort((a, b) => millis(b.at) - millis(a.at)),
    [logs],
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="every outgoing LLM prompt for this run (testing)"
        className="text-[11px] uppercase tracking-widest text-violet-300/70 hover:text-violet-200 border border-violet-500/30 hover:border-violet-400/50 rounded px-2 py-1 leading-none"
      >
        📝 prompts
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-slate-900 border border-slate-700 rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-lg text-slate-100">
                LLM prompts <span className="text-slate-500 text-sm">— {ordered.length} calls, latest first</span>
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-slate-500 hover:text-slate-200 text-lg leading-none flex-none"
                title="close"
              >
                ✕
              </button>
            </div>

            {ordered.length === 0 && (
              <p className="text-sm text-slate-500">No prompts logged yet — guide a character or turn the page.</p>
            )}

            {ordered.map((log, i) => (
              <details key={i} open={i === 0} className="rounded-lg border border-slate-800 bg-slate-950/40">
                <summary className="cursor-pointer px-3 py-2 text-sm select-none">
                  <span className={`uppercase tracking-widest text-[11px] ${TYPE_COLOR[log.type] ?? 'text-slate-300'}`}>
                    {log.type}
                  </span>{' '}
                  <span className="text-slate-600">· {log.model}</span>
                </summary>
                <div className="px-3 pb-3 space-y-3">
                  {log.system && <Block label="system" text={log.system} />}
                  {log.prompt && <Block label="prompt" text={log.prompt} />}
                  {log.messages && (
                    <div className="space-y-1.5">
                      <span className="text-[10px] uppercase tracking-widest text-slate-500">messages</span>
                      {log.messages.map((m, j) => (
                        <pre
                          key={j}
                          className="whitespace-pre-wrap break-words rounded-md bg-black/40 p-2 font-mono text-[11px] leading-snug text-slate-400"
                        >
                          <span className="text-violet-300/70">[{m.role}] </span>
                          {m.content}
                        </pre>
                      ))}
                    </div>
                  )}
                  {log.response && <Block label="response" text={log.response} tone="text-emerald-300/70" />}
                  {log.meta && <Block label="meta" text={JSON.stringify(log.meta, null, 2)} />}
                </div>
              </details>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function Block({ label, text, tone = 'text-slate-400' }: { label: string; text: string; tone?: string }) {
  return (
    <div className="space-y-1">
      <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <pre
        className={`max-h-[45vh] overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-black/40 p-3 font-mono text-[11px] leading-snug ${tone}`}
      >
        {text}
      </pre>
    </div>
  );
}
